import React from "react";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseSelect from "../../BaseComponents/BaseSelect";
import MultiSelect from "../../BaseComponents/BaseSelect/MultiSelect";
import { formFields, hrEnums } from "../../Constant/HR_Staff/hrEnums";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import { digitRegex, SelectPlaceHolder } from "../../Constant/validation";
import { ContactNo, Designation, Email, SuperAdmin } from "../../Constant";
import { getLocalItem, RequiredSign } from "../../Constant/common";
import { ProfileSection, InfoField } from "../../BaseComponents/ProfileSection";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import { User, RefreshCw } from "react-feather";
import { staffEnums } from "../../Constant/Staff/staff";

const ProfileDetails = ({
  staffForm,
  viewProfileMode,
  handleProfileEdit,
  handleProfileDiscard,
  editProfileMode,
  roleData,
  centerData,
  fetchReportingManager,
  reportingManager,
  lastStaffId,
  btnLoader,
  staffData,
  handleMultiCenter,
  selectedMultiCenter,
  isProfile,
  selectedCenterIds,
  isEditMode,
  subscriptionData,
  organizationCenters,
  organizationDesignations,
  isSuperAdmin,
  onResetEmailClick,
}) => {
  // Read role inside the component so it always reflects the current localStorage value
  const currentRole = getLocalItem("role");

  const isSaveDisabled = React.useMemo(() => {
    const fields = [
      "first_name",
      "middle_name",
      "last_name",
      "designation",
      "role",
      "subscription",
      "staff_type",
      "email",
      "contact_no",
      "center",
      "primary_center_id",
      "reporting_manager",
      "staff_id",
    ];

    return !fields.some((field) => {
      const val = staffForm.values[field];
      const initVal = staffForm.initialValues[field];

      if (Array.isArray(val) || Array.isArray(initVal)) {
        const valArr = Array.isArray(val) ? val : [];
        const initArr = Array.isArray(initVal) ? initVal : [];
        if (valArr.length !== initArr.length) return true;
        return valArr.some((item, idx) => item !== initArr[idx]);
      }

      const normalizedVal = val === null || val === undefined ? "" : String(val).trim();
      const normalizedInitVal =
        initVal === null || initVal === undefined ? "" : String(initVal).trim();
      return normalizedVal !== normalizedInitVal;
    });
  }, [staffForm.values, staffForm.initialValues]);

  const canResetEmail =
    !isProfile &&
    onResetEmailClick &&
    ["SuperAdmin", "Admin", "HR"].some((r) => currentRole?.trim()?.includes(r));

  return (
    <ProfileSection
      id="profile-info"
      title={hrEnums.PROFILE_INFO}
      icon={User}
      iconColor="#6366f1"
      viewMode={viewProfileMode}
      editMode={editProfileMode}
      onEdit={handleProfileEdit}
      onDiscard={handleProfileDiscard}
      isEditable={true}
      btnLoader={btnLoader}
      animDelay="0ms"
      isSaveDisabled={isSaveDisabled}
    >
      {viewProfileMode ? (
        <div className="row">
          <div className="col-6 col-md-3">
            <InfoField label={hrEnums.STAFF_NAME} value={staffData?.name} required />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.ID}
              value={staffData?.staffGeneralInformation?.staff_id}
              mono
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField label={Designation} value={staffData?.position?.position_name} required />
          </div>
          <div className="col-6 col-md-3">
            <InfoField label={hrEnums.ROLE} value={staffData?.roleModel?.role_name} required />
          </div>
          <div className="col-6 col-md-3">
            <InfoField label={hrEnums.STAFF_TYPE} value={staffData?.staff_type} />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={Email}
              value={staffData?.email}
              required
              sideAction={
                canResetEmail && staffData?.email ? (
                  <BaseTooltip text="Reset Email" position="top">
                    <button
                      type="button"
                      onClick={onResetEmailClick}
                      aria-label="Reset Email"
                      style={{
                        height: 35,
                        width: 35,
                        borderRadius: 8,
                        background: "rgba(99,102,241,0.1)",
                        border: "1px solid rgba(99,102,241,0.25)",
                        color: "var(--brand-indigo)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                        flexShrink: 0,
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(99,102,241,0.2)";
                        e.currentTarget.style.borderColor = "rgba(99,102,241,0.4)";
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(99,102,241,0.1)";
                        e.currentTarget.style.borderColor = "rgba(99,102,241,0.25)";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <RefreshCw size={14} strokeWidth={2.2} />
                    </button>
                  </BaseTooltip>
                ) : null
              }
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField label={ContactNo} value={staffData?.contact_no} />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.CENTER}
              value={staffData?.center?.map((c) => c.center_name).join(", ")}
              required
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={staffEnums.ORGANIZATION}
              value={staffData?.organization?.organization_name}
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField label={staffEnums.SUBSCRIPTION} value={staffData?.subscription?.plan_name} />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.PRIMARY_CENTER}
              value={staffData?.primaryCenter?.center_name}
              required
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField label={hrEnums.REPORTING_MANAGER} value={staffData?.reportingTo?.name} />
          </div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              label={
                <>
                  {hrEnums.FIRST_NAME}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              name={formFields.first_name}
              type="text"
              placeholder={PlaceHolderFormat(hrEnums.FIRST_NAME)}
              value={staffForm.values.first_name}
              touched={staffForm.touched.first_name}
              error={staffForm.errors.first_name}
              handleBlur={staffForm.handleBlur}
              handleChange={staffForm.handleChange}
              disabled={btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              label={hrEnums.MIDDLE_NAME}
              name={formFields.middle_name}
              type="text"
              placeholder={PlaceHolderFormat(hrEnums.MIDDLE_NAME)}
              value={staffForm.values.middle_name}
              touched={staffForm.touched.middle_name}
              error={staffForm.errors.middle_name}
              handleBlur={staffForm.handleBlur}
              handleChange={staffForm.handleChange}
              disabled={btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              label={
                <>
                  {hrEnums.LAST_NAME}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              name={formFields.last_name}
              type="text"
              placeholder={PlaceHolderFormat(hrEnums.LAST_NAME)}
              value={staffForm.values.last_name}
              touched={staffForm.touched.last_name}
              error={staffForm.errors.last_name}
              handleBlur={staffForm.handleBlur}
              handleChange={staffForm.handleChange}
              disabled={btnLoader}
            />
          </div>
          {!isSuperAdmin && (
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.designation}
                name={formFields.designation}
                label={
                  <>
                    {Designation}
                    <span className="text-danger ms-1">{RequiredSign}</span>
                  </>
                }
                options={organizationDesignations}
                placeholder={SelectPlaceHolder(Designation)}
                handleChange={(f, v) => staffForm.setFieldValue(f, v)}
                handleBlur={() => staffForm.setFieldTouched(Designation, true)}
                value={staffForm.values.designation}
                touched={staffForm.touched.designation}
                error={staffForm.errors.designation}
                isDisabled={isProfile || !staffForm.values.organization || btnLoader}
              />
            </div>
          )}
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseSelect
              id={formFields.role}
              name={formFields.role}
              label={
                <>
                  {hrEnums.ROLE}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              options={roleData}
              placeholder={SelectPlaceHolder(hrEnums.ROLE)}
              handleChange={(f, v) => staffForm.setFieldValue(f, v)}
              handleBlur={() => staffForm.setFieldTouched(hrEnums.ROLE, true)}
              value={staffForm.values.role}
              touched={staffForm.touched.role}
              error={staffForm.errors.role}
              isDisabled={isProfile || btnLoader}
            />
          </div>
          {currentRole === SuperAdmin && (
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.subscription}
                name={formFields.subscription}
                label={staffEnums.SUBSCRIPTION}
                options={subscriptionData}
                placeholder={SelectPlaceHolder(staffEnums.SUBSCRIPTION)}
                value={staffForm.values.subscription}
                handleChange={(f, v) => staffForm.setFieldValue(f, v ?? null)}
                handleBlur={() => staffForm.setFieldTouched("subscription", true)}
                touched={staffForm.touched.subscription}
                error={staffForm.errors.subscription}
                isDisabled={btnLoader}
              />
            </div>
          )}
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseSelect
              id={formFields.staff_type}
              name={formFields.staff_type}
              label={
                <>
                  {hrEnums.STAFF_TYPE}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              options={hrEnums.STAFF_TYPE_OP}
              placeholder={SelectPlaceHolder(hrEnums.STAFF_TYPE)}
              handleChange={(f, v) => staffForm.setFieldValue(f, v)}
              handleBlur={() => staffForm.setFieldTouched(hrEnums.STAFF_TYPE, true)}
              value={staffForm.values.staff_type}
              touched={staffForm.touched.staff_type}
              error={staffForm.errors.staff_type}
              isDisabled={true}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <label className="form-label">
              {Email}
              <span className="text-danger ms-1">{RequiredSign}</span>
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ flex: 1 }}>
                <input
                  name={formFields.email}
                  type="text"
                  placeholder={PlaceHolderFormat(Email)}
                  value={staffForm.values.email || ""}
                  onBlur={staffForm.handleBlur}
                  onChange={staffForm.handleChange}
                  disabled={isProfile || isEditMode || btnLoader}
                  className={`form-control ${staffForm.touched.email && staffForm.errors.email ? "is-invalid" : ""}`}
                  style={{ minHeight: 38 }}
                />
              </div>
              {canResetEmail && isEditMode && (
                <BaseTooltip text="Reset Email" position="top">
                  <button
                    type="button"
                    onClick={onResetEmailClick}
                    aria-label="Reset Email"
                    style={{
                      height: 38,
                      width: 38,
                      borderRadius: 8,
                      background: "rgba(99,102,241,0.1)",
                      border: "1px solid rgba(99,102,241,0.25)",
                      color: "var(--brand-indigo)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      padding: 0,
                      flexShrink: 0,
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(99,102,241,0.2)";
                      e.currentTarget.style.borderColor = "rgba(99,102,241,0.4)";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(99,102,241,0.1)";
                      e.currentTarget.style.borderColor = "rgba(99,102,241,0.25)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <RefreshCw size={14} strokeWidth={2.2} />
                  </button>
                </BaseTooltip>
              )}
            </div>
            {staffForm.touched.email && staffForm.errors.email && (
              <div className="invalid-feedback d-block">{staffForm.errors.email}</div>
            )}
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              label={
                <>
                  {ContactNo}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              name={formFields.contact_no}
              type="text"
              placeholder={PlaceHolderFormat(ContactNo)}
              value={staffForm.values.contact_no}
              touched={staffForm.touched.contact_no}
              error={staffForm.errors.contact_no}
              handleBlur={staffForm.handleBlur}
              maxLength={10}
              handleChange={(e) => {
                if (digitRegex.test(e?.target?.value)) staffForm.handleChange(e);
              }}
              disabled={btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <MultiSelect
              id={formFields.center}
              name={formFields.center}
              label={
                <>
                  {hrEnums.CENTER}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              options={isSuperAdmin ? centerData : organizationCenters}
              placeholder={SelectPlaceHolder(hrEnums.CENTER)}
              handleBlur={staffForm.handleBlur}
              onChange={handleMultiCenter}
              value={selectedMultiCenter ?? null}
              touched={staffForm.touched.center}
              error={staffForm.errors.center}
              isDisabled={isProfile || btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseSelect
              id={formFields.primary_center_id}
              name={formFields.primary_center_id}
              label={hrEnums.PRIMARY_CENTER}
              options={selectedCenterIds}
              placeholder={SelectPlaceHolder(hrEnums.PRIMARY_CENTER)}
              handleChange={(f, v) => {
                staffForm.setFieldValue(f, v);
                fetchReportingManager(v);
              }}
              handleBlur={() => staffForm.setFieldTouched(hrEnums.PRIMARY_CENTER, true)}
              value={staffForm.values.primary_center_id}
              touched={staffForm.touched.primary_center_id}
              error={staffForm.errors.primary_center_id}
              isDisabled={isProfile || btnLoader}
              required
            />
          </div>
          {!isSuperAdmin && (
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.reporting_manager}
                name={formFields.reporting_manager}
                label={hrEnums.REPORTING_MANAGER}
                options={reportingManager}
                placeholder={SelectPlaceHolder(hrEnums.REPORTING_MANAGER)}
                handleChange={(f, v) => staffForm.setFieldValue(f, v)}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.REPORTING_MANAGER, true)}
                value={staffForm.values.reporting_manager}
                touched={staffForm.touched.reporting_manager}
                error={staffForm.errors.reporting_manager}
                isDisabled={isProfile || btnLoader}
              />
            </div>
          )}
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              label={
                <>
                  {hrEnums.STAFF_ID}
                  <span className="text-danger ms-1">{RequiredSign}</span>
                </>
              }
              name={formFields.staff_id}
              type="text"
              placeholder={PlaceHolderFormat(hrEnums.STAFF_ID)}
              value={staffForm.values.staff_id}
              touched={staffForm.touched.staff_id}
              error={staffForm.errors.staff_id}
              handleBlur={staffForm.staff_id}
              handleChange={staffForm.handleChange}
              disabled
            />
            {!editProfileMode && lastStaffId?.charAt(4) !== "0" && (
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4 }}>
                {hrEnums.LAST_STAFF_ID}
                {lastStaffId}
              </div>
            )}
          </div>
        </div>
      )}
    </ProfileSection>
  );
};

export default ProfileDetails;
